/**
 * Operator-friendly labels + units for every telemetry key the EMS
 * server might publish. The Historical analyzer's "Browse all telemetry"
 * panel reads this when rendering each device's metric list.
 *
 * Order of resolution:
 *   1. Exact-match in {@link KEY_LABELS} — wins.
 *   2. AE2 bucketed-derivative pattern (`EP_d`, `EN_h`, `P_EP_5`, etc.) —
 *      synthesized via {@link inferAggregateLabel}.
 *   3. Suffix-based fallback (`_kW` → "Power (kW)", `_V` → "Voltage (V)").
 *
 * Adding a new published key only requires entering it here — the
 * adapter discovers raw keys from the TB `/keys/timeseries` endpoint and
 * the panel labels them automatically.
 */

export interface KeyMeta {
  label: string;
  unit: string;
  /** Multiplier applied at plot time (e.g. 1 W → 0.001 kW). */
  scale?: number;
  /** Negate after scaling (for EMS-convention sign flips). */
  signFlip?: boolean;
  /** "line" (default) or "step" for discrete signals. */
  chart?: "line" | "step";
}

/* -------------------------------------------------------------------------- */
/* Exact-match dictionary                                                     */
/* -------------------------------------------------------------------------- */

export const KEY_LABELS: Record<string, KeyMeta> = {
  // ── Grid / utility device ──────────────────────────────────────────
  grid_kW:        { label: "Active power",        unit: "kW",              chart: "line" },  // canonical: +import
  grid_kVAr:      { label: "Reactive power",      unit: "kVAr",            chart: "line" },
  total_kW:       { label: "Total active power",  unit: "kW",              chart: "line" },  // canonical: +consumption
  current_A:      { label: "Line current",        unit: "A",               chart: "line" },
  frequency_Hz:   { label: "Grid frequency",      unit: "Hz",              chart: "line" },
  voltage_V:      { label: "Voltage",             unit: "V",               chart: "line" },
  voltage_V_L1:   { label: "Voltage (L1)",        unit: "V",               chart: "line" },
  voltage_V_L2:   { label: "Voltage (L2)",        unit: "V",               chart: "line" },
  voltage_V_L3:   { label: "Voltage (L3)",        unit: "V",               chart: "line" },
  PF_total:       { label: "Power factor",        unit: "",                chart: "line" },

  // ── BESS device ─────────────────────────────────────────────────────
  battery_kW:           { label: "Active power",          unit: "kW",   scale: -1, chart: "line" },   // EMS:+discharge → SPA:+charge
  battery_kVAr:         { label: "Reactive power",        unit: "kVAr",            chart: "line" },
  "battery_soc_%":      { label: "State of charge",       unit: "%",               chart: "line" },
  "battery_soh_%":      { label: "State of health",       unit: "%",               chart: "line" },
  total_voltage_V:      { label: "DC bus voltage",        unit: "V",               chart: "line" },
  ambient_temperature_C: { label: "Ambient temperature",  unit: "°C",              chart: "line" },
  cell_temp_C:          { label: "Cell temperature",      unit: "°C",              chart: "line" },
  fault_count:          { label: "Fault count",           unit: "",                chart: "step" },
  bess_onoff:           { label: "ON / OFF state",        unit: "",                chart: "step" },
  EP:                   { label: "Charged energy (counter)",    unit: "kWh", chart: "line" },
  EN:                   { label: "Discharged energy (counter)", unit: "kWh", chart: "line" },

  // ── Controller device ───────────────────────────────────────────────
  ctrl_pwr_kW:           { label: "Commanded power (aggregate)",   unit: "kW",   chart: "step" },
  ctrl_pwr_kVAr:         { label: "Commanded reactive (aggregate)", unit: "kVAr", chart: "step" },
  ctrl_pwr_kW_1:         { label: "Commanded power · BESS #1",     unit: "kW",   chart: "step" },
  ctrl_pwr_kW_2:         { label: "Commanded power · BESS #2",     unit: "kW",   chart: "step" },
  ctrl_pwr_kW_3:         { label: "Commanded power · BESS #3",     unit: "kW",   chart: "step" },
  ctrl_pwr_kW_4:         { label: "Commanded power · BESS #4",     unit: "kW",   chart: "step" },
  ctrl_pwr_kVAr_1:       { label: "Commanded reactive · BESS #1",  unit: "kVAr", chart: "step" },
  ctrl_pwr_kVAr_2:       { label: "Commanded reactive · BESS #2",  unit: "kVAr", chart: "step" },
  ctrl_pwr_kVAr_3:       { label: "Commanded reactive · BESS #3",  unit: "kVAr", chart: "step" },
  ctrl_pwr_kVAr_4:       { label: "Commanded reactive · BESS #4",  unit: "kVAr", chart: "step" },
  pid_setpoint_W:        { label: "PID setpoint (active)",         unit: "W",    chart: "step" },
  pid_setpoint_VAr:      { label: "PID setpoint (reactive)",       unit: "VAr",  chart: "step" },
  mode_active:           { label: "Active mode",                   unit: "",     chart: "step" },
  mode_reactive:         { label: "Reactive mode",                 unit: "",     chart: "step" },
  auto_bess_on:          { label: "Auto BESS on",                  unit: "",     chart: "step" },
  pid_actively_controlling: { label: "PID actively controlling",   unit: "",     chart: "step" },
  schedule_source:       { label: "Schedule source",               unit: "",     chart: "step" },

  // ── PV device ───────────────────────────────────────────────────────
  pv_power_kW: { label: "Generation", unit: "kW", chart: "line" },
};

/* -------------------------------------------------------------------------- */
/* AE2-derivative pattern                                                     */
/*                                                                            */
/* AnalyticsEngineV2 publishes bucketed deltas + power averages + cumulative  */
/* consumption on each per-BESS device. The names follow rigid patterns we    */
/* can decode without enumerating every combination:                          */
/*   EP / EN ........................ cumulative charge / discharge counter  */
/*   EP_<g> / EN_<g> ............... kWh delivered in the bucket             */
/*   EP_<g>_abs / EN_<g>_abs ...... absolute (charge + discharge) bucket    */
/*   EP_<g>_consumption ........... cumulative consumed-per-period sum      */
/*   P_EP_5 / P_EN_5 .............. 5-min average power in W                */
/*   Etot_<g> / Etot_<g>_abs ...... throughput (|charge| + |discharge|)    */
/* -------------------------------------------------------------------------- */

const PERIOD_LABEL: Record<string, string> = {
  "15":     "15-min bucket",
  h:        "hourly bucket",
  d:        "daily bucket",
  w:        "weekly bucket",
  m:        "monthly bucket",
  y:        "yearly bucket",
};
const COUNTER_LABEL: Record<string, string> = {
  EP:   "Charged",
  EN:   "Discharged",
  Etot: "Total throughput",
};

const RE_DELTA = /^(EP|EN|Etot)_(15|h|d|w|m|y)$/;
const RE_ABS   = /^(EP|EN|Etot)_(h|d|w|m|y)_abs$/;
const RE_CONS  = /^(EP|EN|Etot)_(d|w|m|y)_consumption$/;
const RE_POW   = /^P_(EP|EN)_5$/;

export function inferAggregateLabel(key: string): KeyMeta | null {
  let m: RegExpMatchArray | null;
  m = RE_DELTA.exec(key);
  if (m) {
    const c = m[1] as keyof typeof COUNTER_LABEL;
    return { label: `${COUNTER_LABEL[c]} (${PERIOD_LABEL[m[2]]})`, unit: "kWh", chart: "line" };
  }
  m = RE_ABS.exec(key);
  if (m) {
    const c = m[1] as keyof typeof COUNTER_LABEL;
    return { label: `${COUNTER_LABEL[c]} (absolute, ${PERIOD_LABEL[m[2]]})`, unit: "kWh", chart: "line" };
  }
  m = RE_CONS.exec(key);
  if (m) {
    const c = m[1] as keyof typeof COUNTER_LABEL;
    return { label: `${COUNTER_LABEL[c]} (${PERIOD_LABEL[m[2]]}, cumulative)`, unit: "kWh", chart: "line" };
  }
  m = RE_POW.exec(key);
  if (m) {
    const c = m[1] as keyof typeof COUNTER_LABEL;
    return { label: `${COUNTER_LABEL[c]} average power (5 min)`, unit: "W", chart: "line" };
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Suffix fallback                                                            */
/* -------------------------------------------------------------------------- */

const SUFFIX_HINTS: Array<[RegExp, KeyMeta]> = [
  [/_kW$/,    { label: "Power",         unit: "kW",   chart: "line" }],
  [/_kVAr$/,  { label: "Reactive",      unit: "kVAr", chart: "line" }],
  [/_kWh$/,   { label: "Energy",        unit: "kWh",  chart: "line" }],
  [/_W$/,     { label: "Power",         unit: "W",    chart: "line" }],
  [/_VAr$/,   { label: "Reactive",      unit: "VAr",  chart: "line" }],
  [/_Wh$/,    { label: "Energy",        unit: "Wh",   chart: "line" }],
  [/_V$/,     { label: "Voltage",       unit: "V",    chart: "line" }],
  [/_A$/,     { label: "Current",       unit: "A",    chart: "line" }],
  [/_Hz$/,    { label: "Frequency",     unit: "Hz",   chart: "line" }],
  [/_C$/,     { label: "Temperature",   unit: "°C",   chart: "line" }],
  [/_%$/,     { label: "Percent",       unit: "%",    chart: "line" }],
];

/**
 * Resolve a key to its operator-friendly label + unit. Falls through:
 *   exact match → AE2 pattern → suffix hint → raw key with empty unit.
 */
export function labelForKey(key: string): KeyMeta {
  if (KEY_LABELS[key]) return KEY_LABELS[key];
  const ae2 = inferAggregateLabel(key);
  if (ae2) return ae2;
  for (const [re, meta] of SUFFIX_HINTS) {
    if (re.test(key)) {
      // Use the raw key as the primary label; the suffix hint supplies
      // the unit + the generic noun (so a future "battery_kW_phase_a"
      // still reads sensibly).
      return { ...meta, label: humanize(key) };
    }
  }
  return { label: humanize(key), unit: "", chart: "line" };
}

/** "battery_soc_%" → "battery soc %"; "ctrl_pwr_kW_2" → "ctrl pwr kW 2". */
function humanize(key: string): string {
  return key.replace(/_/g, " ");
}
